import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, RotateCcw } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

/**
 * Phase 4 plan 04-05: tri-state field primitive for inheritance UX.
 *
 * Renders three visual states driven by the `inherited` prop (RESEARCH §5.2):
 *   inherited=true  -> muted "Herdado do workflow: {value}" + Substituir link
 *   inherited=false -> <renderInput/> + Substituído badge + Voltar ao padrão link
 *   editing         -> same visual as overridden; parent drives focus
 *
 * Wire up with per-field inheritance blobs from schemas/workflow-node.ts.
 */
export interface InheritableFieldProps<T> {
  label: string
  inheritedValue: T
  value: T | null
  inherited: boolean
  onChange: (value: T, inherited: boolean) => void
  renderInput: (p: {
    value: T
    onChange: (v: T) => void
    disabled: boolean
  }) => ReactNode
  formatDisplay?: (v: T) => string
  disabled?: boolean
}

export function InheritableField<T>({
  label,
  inheritedValue,
  value,
  inherited,
  onChange,
  renderInput,
  formatDisplay,
  disabled,
}: InheritableFieldProps<T>) {
  const { t } = useTranslation('workflows')
  const display = formatDisplay ?? ((v: T) => String(v))

  if (inherited) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-300">{label}</Label>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">
            {t('inheritance.inheritedFrom', { value: '​' }).replace(
              '​',
              ''
            )}
          </span>
          <span className="text-sm text-gray-300">
            {display(inheritedValue)}
          </span>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0 py-0 text-indigo-400"
            onClick={() => onChange(inheritedValue, false)}
            disabled={disabled}
          >
            <Pencil className="h-3 w-3 mr-1" />
            {t('inheritance.override')}
          </Button>
        </div>
      </div>
    )
  }

  const currentValue = (value ?? inheritedValue) as T
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-semibold text-gray-300">{label}</Label>
        <Badge variant="outline" className="border-gray-700/50 text-gray-300">
          {t('inheritance.overridden')}
        </Badge>
      </div>
      {renderInput({
        value: currentValue,
        onChange: (v) => onChange(v, false),
        disabled: !!disabled,
      })}
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto px-0 py-0 text-indigo-400"
        onClick={() => onChange(inheritedValue, true)}
        disabled={disabled}
      >
        <RotateCcw className="h-3 w-3 mr-1" />
        {t('inheritance.resetToDefault')}
      </Button>
    </div>
  )
}
